class LucidiusInfiniteScroll extends HTMLElement {
  connectedCallback() {
    this.grid = this.closest('#ProductGridContainer')?.querySelector('#product-grid');
    this.link = this.querySelector('[data-load-more]');
    this.status = this.querySelector('[data-load-status]');
    if (!this.grid || !this.link || !('IntersectionObserver' in window)) return;
    this.querySelector('[data-pagination-fallback]').hidden = true;
    this.link.hidden = false;
    this.onClick = event => {
      event.preventDefault();
      this.loadMore(true);
    };
    this.onReset = () => {
      this.stale = true;
      this.observer.disconnect();
      this.controller?.abort();
    };
    this.link.addEventListener('click', this.onClick);
    window.addEventListener('lucidius:collection-updating', this.onReset);
    this.observer = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) this.loadMore();
    }, { rootMargin: '400px 0px' });
    this.observer.observe(this.link);
  }

  disconnectedCallback() {
    this.stale = true;
    this.observer?.disconnect();
    this.controller?.abort();
    this.link?.removeEventListener('click', this.onClick);
    window.removeEventListener('lucidius:collection-updating', this.onReset);
  }

  async loadMore(manual = false) {
    if (this.loading || this.stale || !this.link?.getAttribute('href')) return;
    this.loading = true;
    this.observer.unobserve(this.link);
    this.link.setAttribute('aria-disabled', 'true');
    this.grid.setAttribute('aria-busy', 'true');
    this.status.textContent = this.dataset.loading;
    this.controller = new AbortController();
    const url = new URL(this.link.href);
    url.searchParams.set('section_id', this.dataset.sectionId);
    try {
      const response = await fetch(url, { signal: this.controller.signal });
      if (!response.ok) throw new Error('Collection page unavailable');
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      if (this.stale || !this.isConnected) return;
      const sourceGrid = html.querySelector('#product-grid');
      if (!sourceGrid || sourceGrid.dataset.id !== this.grid.dataset.id) throw new Error('Collection grid unavailable');
      const ids = new Set([...this.grid.children].map(item => item.dataset.productId));
      const items = [...sourceGrid.children].filter(item => !ids.has(item.dataset.productId));
      if (!items.length) throw new Error('Collection products unavailable');
      items.forEach(item => {
        item.querySelectorAll('img').forEach(image => image.loading = 'lazy');
        item.classList.add('scroll-trigger--cancel');
      });
      this.grid.append(...items);
      this.status.textContent = this.dataset.loaded.replace('[count]', this.grid.children.length);
      const next = html.querySelector('lucidius-infinite-scroll [data-load-more]');
      if (next) {
        this.link.href = next.href;
        this.observer.observe(this.link);
      } else {
        this.link.removeAttribute('href');
        this.link.hidden = true;
        if (manual) this.status.tabIndex = -1;
      }
      if (manual) {
        const target = items[0].querySelector('a');
        target?.focus({ preventScroll: true });
      }
      window.dispatchEvent(new CustomEvent('lucidius:products-appended', { detail: { count: items.length } }));
    } catch (error) {
      if (error.name !== 'AbortError') this.status.textContent = this.dataset.error;
    } finally {
      this.loading = false;
      this.link.removeAttribute('aria-disabled');
      this.grid.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('lucidius-infinite-scroll')) customElements.define('lucidius-infinite-scroll', LucidiusInfiniteScroll);
